import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  RefreshCw, Users, XCircle, CheckCircle, DollarSign, Download, Calendar, AlertTriangle, Clock, Trash2, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MONTHS = [
  { value: 'all', label: 'All months' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active_only', label: 'Active + Authenticated only' },
  { value: 'duplicates', label: '⚠️ Duplicate subscribers only' },
  { value: 'active', label: 'Active' },
  { value: 'authenticated', label: 'Authenticated' },
  { value: 'created', label: 'Created (pending / abandoned)' },
  { value: 'halted', label: 'Halted (failed)' },
  { value: 'cancelled', label: 'Cancelled' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const monthOf = (iso) => {
  if (!iso) return null;
  try {
    const m = new Date(iso).getMonth() + 1;
    return String(m).padStart(2, '0');
  } catch {
    return null;
  }
};

const yearOf = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).getFullYear(); } catch { return null; }
};

const AutopayManagement = ({ sessionToken }) => {
  const [view, setView] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, cancelled: 0 });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  // Filters
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active_only');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, plansRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/autopay/subscriptions`),
        fetch(`${API_URL}/api/autopay/plans`)
      ]);
      const subsData = await subsRes.json();
      const plansData = await plansRes.json();
      setSubscriptions(subsData.subscriptions || []);
      setStats(subsData.stats || {});
      setPlans(plansData.plans || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Cancel this autopay subscription?')) return;
    setCancelling(subscriptionId);
    try {
      const res = await fetch(`${API_URL}/api/autopay/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription_id: subscriptionId, cancel_at_cycle_end: true })
      });
      const data = await res.json();
      if (data.status === 'success') fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setCancelling(null);
    }
  };

  const cleanupStale = async () => {
    const daysStr = window.prompt(
      "Delete 'created'-status autopay records older than how many days?\n\n" +
      "These are abandoned checkouts where the user never authenticated the e-mandate — they will never debit anyone.\n\n" +
      "Recommended: 7",
      "7"
    );
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (!days || days < 1) {
      window.alert('Enter a positive number of days.');
      return;
    }
    if (!window.confirm(`Delete all 'created' autopay records older than ${days} ${days === 1 ? 'day' : 'days'}? This cannot be undone.`)) return;
    setCleaning(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/autopay/cleanup-stale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days })
      });
      const data = await res.json();
      if (data.status === 'success') {
        window.alert(`Cleaned up ${data.deleted_count} stale ${data.deleted_count === 1 ? 'record' : 'records'} (older than ${days} ${days === 1 ? 'day' : 'days'}).`);
        fetchData();
      } else {
        window.alert('Cleanup failed: ' + (data.detail || data.message || 'unknown error'));
      }
    } catch (err) {
      console.error(err);
      window.alert('Cleanup error: ' + err.message);
    } finally {
      setCleaning(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      active: { className: 'bg-green-100 text-green-800 border-green-300', label: 'Active' },
      authenticated: { className: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Authenticated' },
      cancelled: { className: 'bg-red-100 text-red-800 border-red-300', label: 'Cancelled' },
      halted: { className: 'bg-orange-100 text-orange-800 border-orange-300', label: 'Halted' },
      pending: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pending' },
      created: { className: 'bg-gray-100 text-gray-800 border-gray-300', label: 'Created' },
    };
    const s = map[status] || { className: 'bg-gray-100 text-gray-800', label: status || 'Unknown' };
    return <Badge className={`border ${s.className}`}>{s.label}</Badge>;
  };

  // Years present in data (for year filter)
  const availableYears = useMemo(() => {
    const set = new Set();
    subscriptions.forEach(s => {
      const y = yearOf(s.current_end) || yearOf(s.charge_at) || yearOf(s.created_at);
      if (y) set.add(y);
    });
    return ['all', ...Array.from(set).sort((a, b) => b - a)];
  }, [subscriptions]);

  // Compute "next debit date" for each row: charge_at preferred, else current_end
  const enriched = useMemo(() => subscriptions.map(s => {
    const next = s.charge_at || s.current_end || null;
    return { ...s, _next_debit: next };
  }), [subscriptions]);

  // Identify duplicate ACTIVE/AUTHENTICATED subscribers (same email OR phone with >1 active)
  const duplicateKeys = useMemo(() => {
    const byEmail = new Map();
    const byPhone = new Map();
    enriched.forEach(s => {
      if (s.status !== 'active' && s.status !== 'authenticated') return;
      const e = (s.user_email || '').trim().toLowerCase();
      const p = (s.user_phone || '').replace(/\D/g, '').slice(-10);
      if (e) byEmail.set(e, (byEmail.get(e) || 0) + 1);
      if (p) byPhone.set(p, (byPhone.get(p) || 0) + 1);
    });
    const dupEmails = new Set([...byEmail.entries()].filter(([, c]) => c > 1).map(([k]) => k));
    const dupPhones = new Set([...byPhone.entries()].filter(([, c]) => c > 1).map(([k]) => k));
    return { dupEmails, dupPhones };
  }, [enriched]);

  const isDuplicate = (s) => {
    if (s.status !== 'active' && s.status !== 'authenticated') return false;
    const e = (s.user_email || '').trim().toLowerCase();
    const p = (s.user_phone || '').replace(/\D/g, '').slice(-10);
    return (e && duplicateKeys.dupEmails.has(e)) || (p && duplicateKeys.dupPhones.has(p));
  };

  const duplicateCount = useMemo(
    () => enriched.filter(isDuplicate).length,
    [enriched, duplicateKeys]
  );

  // Apply filters
  const filtered = useMemo(() => {
    let rows = enriched;

    if (statusFilter === 'active_only') {
      rows = rows.filter(s => s.status === 'active' || s.status === 'authenticated');
    } else if (statusFilter === 'duplicates') {
      rows = rows.filter(isDuplicate);
    } else if (statusFilter !== 'all') {
      rows = rows.filter(s => s.status === statusFilter);
    }

    if (monthFilter !== 'all') {
      rows = rows.filter(s => monthOf(s._next_debit) === monthFilter);
    }
    if (yearFilter !== 'all') {
      rows = rows.filter(s => yearOf(s._next_debit) === Number(yearFilter));
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(s =>
        (s.user_name || '').toLowerCase().includes(q) ||
        (s.user_email || '').toLowerCase().includes(q) ||
        (s.user_phone || '').toLowerCase().includes(q) ||
        (s.subscription_type || '').toLowerCase().includes(q) ||
        (s.razorpay_subscription_id || '').toLowerCase().includes(q)
      );
    }

    // Sort by next debit date ascending (soonest first), nulls last
    return [...rows].sort((a, b) => {
      if (!a._next_debit && !b._next_debit) return 0;
      if (!a._next_debit) return 1;
      if (!b._next_debit) return -1;
      return new Date(a._next_debit) - new Date(b._next_debit);
    });
  }, [enriched, statusFilter, monthFilter, yearFilter, search]);

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Subscription Type', 'Status', 'Amount (₹/cycle)', 'Next Debit Date', 'Current Cycle Start', 'Current Cycle End', 'Razorpay Subscription ID', 'Created At'];
    const rows = filtered.map(s => [
      s.user_name || '',
      s.user_email || '',
      s.user_phone || '',
      s.subscription_type || '',
      s.status || '',
      s.amount ? (s.amount / 100) : '',
      fmtDate(s._next_debit),
      fmtDate(s.current_start),
      fmtDate(s.current_end),
      s.razorpay_subscription_id || '',
      fmtDate(s.created_at),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    // Prefix BOM so Excel opens UTF-8 correctly (₹ symbol)
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const monthLabel = (MONTHS.find(m => m.value === monthFilter)?.label || 'all').replace(/\s+/g, '_');
    a.download = `autopay_subscribers_${monthLabel}_${yearFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isInNextNDays = (iso, n) => {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= n;
  };

  const upcomingCount = useMemo(
    () => filtered.filter(s => isInNextNDays(s._next_debit, 7)).length,
    [filtered]
  );

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="autopay-stats">
        <Card>
          <CardContent className="p-3 text-center">
            <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{stats.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-green-600">{stats.active}</p>
            <p className="text-xs text-gray-500">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-amber-600" data-testid="upcoming-7-days-count">{upcomingCount}</p>
            <p className="text-xs text-gray-500">Debit in next 7 days</p>
          </CardContent>
        </Card>
        <Card className={duplicateCount > 0 ? 'border-orange-300 bg-orange-50/40' : ''}>
          <CardContent className="p-3 text-center">
            <Copy className={`w-5 h-5 mx-auto mb-1 ${duplicateCount > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
            <p className={`text-xl font-bold ${duplicateCount > 0 ? 'text-orange-600' : 'text-gray-500'}`} data-testid="duplicate-count">{duplicateCount}</p>
            <p className="text-xs text-gray-500">Duplicate subscribers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-red-600">{stats.cancelled}</p>
            <p className="text-xs text-gray-500">Cancelled</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm"
          variant={view === 'subscriptions' ? 'default' : 'outline'}
          onClick={() => setView('subscriptions')}
          data-testid="autopay-subs-tab"
        >
          Subscriptions ({subscriptions.length})
        </Button>
        <Button
          size="sm"
          variant={view === 'plans' ? 'default' : 'outline'}
          onClick={() => setView('plans')}
          data-testid="autopay-plans-tab"
        >
          Plans ({plans.length})
        </Button>
        <Button size="sm" variant="outline" onClick={fetchData} data-testid="refresh-autopay-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={cleanupStale}
          disabled={cleaning}
          className="text-red-600 border-red-300 hover:bg-red-50"
          data-testid="cleanup-stale-btn"
          title="Delete 'created'-status records older than 7 days (abandoned checkouts)"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          {cleaning ? 'Cleaning…' : 'Cleanup stale'}
        </Button>
      </div>

      {/* Subscriptions view */}
      {view === 'subscriptions' && (
        <>
          {/* Filters */}
          <Card className="bg-gray-50">
            <CardContent className="p-3">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Debit Month</label>
                  <select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                    data-testid="month-filter"
                  >
                    {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Year</label>
                  <select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                    data-testid="year-filter"
                  >
                    {availableYears.map(y => <option key={y} value={y}>{y === 'all' ? 'All years' : y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                    data-testid="status-filter"
                  >
                    {STATUS_FILTERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-1">
                  <label className="text-xs text-gray-600 mb-1 block">Search (name / email / phone)</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search…"
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                    data-testid="autopay-search"
                  />
                </div>
                <div>
                  <Button
                    size="sm"
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    onClick={exportCSV}
                    disabled={filtered.length === 0}
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> Export CSV
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Showing <span className="font-semibold text-gray-800">{filtered.length}</span> of {subscriptions.length} subscriptions, sorted by next debit date.
              </p>
            </CardContent>
          </Card>

          {/* Subscription cards */}
          <div className="space-y-2" data-testid="autopay-subscriptions-list">
            {filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <RefreshCw className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p>No autopay subscriptions match these filters</p>
                <p className="text-sm mt-1">Try clearing the month/status filters above.</p>
              </div>
            ) : (
              filtered.map(sub => {
                const next = sub._next_debit;
                const soon = isInNextNDays(next, 7);
                const overdue = next && new Date(next) < new Date();
                const dup = isDuplicate(sub);
                return (
                  <Card
                    key={sub.razorpay_subscription_id || sub.id}
                    className={`hover:shadow-md transition-shadow ${dup ? 'border-orange-300 bg-orange-50/40' : soon ? 'border-amber-300 bg-amber-50/40' : ''} ${overdue && sub.status === 'halted' ? 'border-red-300 bg-red-50/40' : ''}`}
                    data-testid={`autopay-sub-${sub.razorpay_subscription_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-semibold text-sm truncate">{sub.user_name || sub.user_email || 'Unknown'}</span>
                            {getStatusBadge(sub.status)}
                            {dup && <Badge className="bg-orange-100 text-orange-800 border border-orange-300" data-testid={`duplicate-badge-${sub.razorpay_subscription_id}`}><Copy className="w-3 h-3 mr-1" />Multiple active</Badge>}
                            {soon && <Badge className="bg-amber-100 text-amber-800 border border-amber-300"><AlertTriangle className="w-3 h-3 mr-1" />Debit soon</Badge>}
                          </div>
                          <div className="text-xs text-gray-600 space-y-0.5">
                            <p>{sub.user_phone || ''} {sub.user_email ? `| ${sub.user_email}` : ''}</p>
                            <p>Type: <span className="font-medium">{sub.subscription_type?.replace(/_/g, ' ') || '-'}</span></p>
                            {sub.amount > 0 && <p>Amount: <span className="font-medium">₹{(sub.amount / 100).toLocaleString('en-IN')}/cycle</span></p>}
                            {sub.razorpay_subscription_id && (
                              <p className="text-gray-400 truncate">Razorpay ID: {sub.razorpay_subscription_id}</p>
                            )}
                          </div>
                        </div>

                        {/* Next Debit Date — prominent column */}
                        <div className="text-right min-w-[140px]" data-testid={`next-debit-${sub.razorpay_subscription_id}`}>
                          <div className="flex items-center justify-end gap-1 text-xs text-gray-500 mb-0.5">
                            <Calendar className="w-3.5 h-3.5" /> Next Debit
                          </div>
                          <div className={`text-base font-bold ${soon ? 'text-amber-700' : overdue ? 'text-red-700' : 'text-gray-900'}`}>
                            {fmtDate(next)}
                          </div>
                          {sub.current_start && (
                            <p className="text-[10px] text-gray-500 mt-1">
                              Cycle: {fmtDate(sub.current_start)} → {fmtDate(sub.current_end)}
                            </p>
                          )}
                        </div>

                        {(sub.status === 'active' || sub.status === 'authenticated') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 border-red-300 hover:bg-red-50"
                            onClick={() => cancelSubscription(sub.razorpay_subscription_id)}
                            disabled={cancelling === sub.razorpay_subscription_id}
                            data-testid={`cancel-sub-${sub.razorpay_subscription_id}`}
                          >
                            {cancelling === sub.razorpay_subscription_id ? '...' : 'Cancel'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Plans List */}
      {view === 'plans' && (
        <div className="space-y-2" data-testid="autopay-plans-list">
          {plans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <DollarSign className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No plans created yet</p>
              <p className="text-sm mt-1">Plans are auto-created when users subscribe with autopay. You can also create them from the Razorpay dashboard.</p>
            </div>
          ) : (
            plans.map(plan => (
              <Card key={plan.id || plan.plan_id} data-testid={`plan-${plan.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{plan.name || plan.subscription_type || plan.item?.name || 'Plan'}</p>
                      <p className="text-xs text-gray-500">
                        ₹{plan.amount || plan.item?.amount ? ((plan.item?.amount || plan.amount) / 100) : '?'}/month
                        {plan.razorpay_plan_id && ` | Razorpay: ${plan.razorpay_plan_id}`}
                      </p>
                    </div>
                    <Badge variant="outline">{plan.period || 'monthly'}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}

          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700">
            <p className="font-medium">How Autopay Plans Work</p>
            <p className="text-xs mt-1">Plans are automatically created in Razorpay when the first user subscribes to a package with auto-renewal. The plan amount matches the package price.</p>
            <p className="text-xs mt-1">Webhook URL for Razorpay: <code className="bg-blue-100 px-1 rounded">{API_URL}/api/autopay/webhook</code></p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutopayManagement;
